// «Спекулятивные страницы»: монотонное число pages и детекция конца данных без server total
// Хранит максимум известных страниц и флаг hasMore по скоупу (в памяти процесса)

import { ScopeKey } from 'shared/utils';

const knownPagesMap = new Map<ScopeKey, number>();
const hasMoreMap = new Map<ScopeKey, boolean>();

export type SpeculativeInput = {
  scopeKey: ScopeKey;
  page: number;
  perPage: number;
  blockOffset: number;
  blockLength: number; // фактически получено элементов в блоке
  requestedLimit?: number; // сколько просили у бэка (limit блока)
};

export type SpeculativeResult = {
  pages: number; // что показывать в пагинаторе
  maxKnownPages: number; // максимум, который уже зафиксирован для скоупа
  hasMore: boolean; // по текущему знанию есть ли ещё данные
  provenNoMore: boolean; // в этом заходе подтвердился конец данных
};

function clampPositiveInt(n: number, min = 1) {
  if (!Number.isFinite(n)) return min;
  const x = Math.floor(n);
  return x < min ? min : x;
}

export function calcSpeculativePages(input: SpeculativeInput): SpeculativeResult {
  const { scopeKey } = input;
  const page = clampPositiveInt(input.page, 1);
  const perPage = clampPositiveInt(input.perPage, 1);
  const blockOffset = Math.max(0, input.blockOffset);
  const blockLength = Math.max(0, input.blockLength);
  const requestedLimit = input.requestedLimit != null ? clampPositiveInt(input.requestedLimit, 1) : undefined;

  // Сколько элементов мы гарантированно знаем на сейчас (включая предыдущие блоки)
  const totalKnownItems = blockOffset + blockLength;
  const knownPagesCandidate = Math.ceil(totalKnownItems / perPage) || 1;

  const prevMax = knownPagesMap.get(scopeKey) ?? 1;
  const maxKnownPages = Math.max(prevMax, knownPagesCandidate, 1);

  // Детекция конца данных:
  // 1) Если offset > 0 и пришло пусто — конец.
  // 2) Если знаем, сколько просили (requestedLimit) и пришло меньше — конец (для блока это надёжный сигнал).
  const endByEmpty = blockLength === 0;
  // Конец даных подтверждаем «укороченной пачкой» только начиная со второго блока (offset>0),
  // чтобы на первом ответе меньше лимита мы всё равно показывали гипотетическую страницу
  const endByShortBatch = requestedLimit != null ? blockLength < requestedLimit : false;
  const provenNoMore = endByEmpty || endByShortBatch;

  const hasMore = hasMoreMap.has(scopeKey) ? Boolean(hasMoreMap.get(scopeKey)) : true;

  // Пока hasMore=true — показываем гипотетическую следующую страницу независимо от позиции пользователя
  const speculativePages = Math.max(maxKnownPages + 1, page + 1);
  const pages = hasMore ? speculativePages : maxKnownPages;

  return { pages, maxKnownPages, hasMore, provenNoMore };
}

export function commitSpeculativeObservation(scopeKey: ScopeKey, obs: { maxKnownPages: number; provenNoMore: boolean }) {
  const prevMax = knownPagesMap.get(scopeKey) ?? 1;
  if (obs.maxKnownPages > prevMax) knownPagesMap.set(scopeKey, obs.maxKnownPages);
  if (obs.provenNoMore) hasMoreMap.set(scopeKey, false);
  else if (!hasMoreMap.has(scopeKey)) hasMoreMap.set(scopeKey, true);
}

export function resetSpeculativeScope(scopeKey: ScopeKey) {
  knownPagesMap.delete(scopeKey);
  hasMoreMap.delete(scopeKey);
}

export function getSpeculativeState(scopeKey: ScopeKey) {
  return {
    pagesMax: knownPagesMap.get(scopeKey) ?? 1,
    hasMore: hasMoreMap.has(scopeKey) ? Boolean(hasMoreMap.get(scopeKey)) : true,
  };
}
